import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ClickAwayListener,
  Collapse,
  Tooltip as MUITooltip,
} from '@mui/material';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { SidebarItem } from 'src/constants/sidebarConstants';

import Button from '../Button/Button';
import Tooltip from '../Tooltip/Tooltip';
import classes from './SidebarLink.module.scss';
import SidebarLinkItem from './SidebarLinkItem/SidebarLinkItem';

export interface SidebarLinkProps {
  link: SidebarItem;
  index: number;
  collapsed?: boolean;
}

const SidebarLink: FC<SidebarLinkProps> = ({
  link,
  index,
  collapsed = false,
}) => {
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const initialOpenActive =
      location.pathname.includes(link.pathname) ||
      link.subitems?.some((el) => location.pathname.includes(el.pathname));
    setOpen(initialOpenActive);

    if (initialOpenActive) {
      //event to listen to
      const event = new CustomEvent('sidebarcategorytoggle', { detail: index });
      document.dispatchEvent(event);
    }
  }, [index, link.pathname, link.subitems, location.pathname, location.search]);

  useEffect(() => {
    const onCategoryOpen = (e: CustomEventInit<number>) => {
      if (e.detail !== index) {
        setOpen(false);
      }
    };

    document.addEventListener('sidebarcategorytoggle', onCategoryOpen);
    return () => {
      document.removeEventListener('sidebarcategorytoggle', onCategoryOpen);
    };
  }, [index]);

  const handleOpen = () => {
    !collapsed && setOpen((prev) => !prev);
  };

  const subitems = link?.subitems?.map((sublink, i) => (
    <SidebarLinkItem key={i} link={sublink} />
  ));

  const subitemsTooltip = (
    <>
      <p>{link?.label}</p>
      <section className={classes.Subitems}>{subitems}</section>
    </>
  );

  return !link?.subitems?.length ? (
    <Tooltip
      tooltipText={link?.label}
      placement="right"
      className={classes.LinkTooltip}
      disabled={!collapsed}
    >
      <SidebarLinkItem
        link={link}
        onClick={() => handleOpen()}
        collapsed={collapsed}
      />
    </Tooltip>
  ) : (
    <>
      <ClickAwayListener
        touchEvent={false}
        onClickAway={() => setShowTooltip(false)}
      >
        <MUITooltip
          classes={{ tooltip: classes.SubTooltip, popper: classes.Popper }}
          placement="right-start"
          onClose={() => setShowTooltip(false)}
          onOpen={collapsed ? () => setShowTooltip(true) : undefined}
          open={showTooltip}
          title={subitemsTooltip}
          enterTouchDelay={0}
          leaveTouchDelay={10000}
        >
          <div
            onClick={() => handleOpen()}
            className={classNames(classes.SidebarLink, classes.SidebarDiv, {
              [classes.Open]: open,
              [classes.HideText]: collapsed,
            })}
          >
            <Button
              startIcon={<FontAwesomeIcon icon={link.icon} />}
              endIcon={
                !collapsed && (
                  <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
                )
              }
              fullWidth
            >
              {!collapsed ? link.label : null}
            </Button>
          </div>
        </MUITooltip>
      </ClickAwayListener>
      <Collapse className={classes.Subitems} in={open && !collapsed}>
        {subitems}
      </Collapse>
    </>
  );
};

export default SidebarLink;
